"use client";

import { useEffect, useRef } from "react";

import { fitCanvas, prefersReducedMotion } from "./motion";
import { useSectionInView } from "./section";

/**
 * The prototype's constellation: a coral focus with blue spokes to drifting
 * points, faint links between near points, and a pulse travelling along each
 * spoke. Behind the hero (focus to the right, where the phone sits) and the
 * closing section (centred, dimmer). Draws only while its Section is in view;
 * under reduced motion it draws one still frame.
 */

interface Options {
  /** Focus point as a fraction of the canvas; spread as a fraction of min(W,H). */
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  /** The same below 700px wide. */
  readonly narrowX: number;
  readonly narrowY: number;
  readonly narrowRadius: number;
  readonly points: number;
  readonly narrowPoints: number;
  readonly alpha: number;
  readonly narrowAlpha: number;
}

const VARIANTS = {
  hero: { x: 0.79, y: 0.5, radius: 0.52, narrowX: 0.5, narrowY: 0.76, narrowRadius: 0.62, points: 30, narrowPoints: 14, alpha: 1, narrowAlpha: 0.5 },
  closing: { x: 0.5, y: 0.5, radius: 1.1, narrowX: 0.5, narrowY: 0.5, narrowRadius: 1.3, points: 22, narrowPoints: 14, alpha: 0.5, narrowAlpha: 0.5 },
} satisfies Record<string, Options>;

type ConstellationVariant = keyof typeof VARIANTS;

interface Point {
  readonly angle: number;
  readonly r: number;
  readonly speed: number;
  readonly z: number;
}

interface Frame {
  width: number;
  height: number;
  points: Point[];
  raf: number;
}

const makePoints = (n: number): Point[] =>
  Array.from({ length: n }, () => ({
    angle: Math.random() * Math.PI * 2,
    r: 0.16 + Math.random() * 0.36,
    speed: (Math.random() < 0.5 ? -1 : 1) * (0.02 + Math.random() * 0.05),
    z: Math.random(),
  }));

const TAU = Math.PI * 2;

export function Constellation({ variant }: { variant: ConstellationVariant }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const running = useSectionInView();

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const options = VARIANTS[variant];
    const reduced = prefersReducedMotion();
    // The ink-coloured dots and links vanish on the dark theme's navy; use
    // cream there. Read once per start — a theme switch re-runs on the next
    // scroll in/out.
    const ink = document.documentElement.classList.contains("dark") ? "250,250,248" : "10,22,40";
    const t0 = performance.now();
    const state: Frame = { width: 0, height: 0, points: [], raf: 0 };

    const draw = (now: number) => {
      const t = (now - t0) / 1000;
      const { width: W, height: H } = state;
      ctx.clearRect(0, 0, W, H);
      const narrow = W < 700;
      const cx = W * (narrow ? options.narrowX : options.x);
      const cy = H * (narrow ? options.narrowY : options.y);
      const R = Math.min(W, H) * (narrow ? options.narrowRadius : options.radius);
      const alpha = narrow ? options.narrowAlpha : options.alpha;
      const placed = state.points.map((p) => {
        const a = p.angle + (reduced ? 0 : t * p.speed);
        return { x: cx + Math.cos(a) * p.r * R * 1.25, y: cy + Math.sin(a) * p.r * R * 0.7, z: p.z };
      });
      placed.forEach((p, i) => {
        const phase = reduced ? 1 : 0.5 + 0.5 * Math.sin(t * 0.8 + i);
        ctx.strokeStyle = `rgba(0,134,191,${(0.05 + 0.14 * phase * p.z) * alpha})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        if (!reduced) {
          const k = (t * 0.3 + i * 0.137) % 1;
          ctx.fillStyle = `rgba(0,134,191,${0.75 * p.z * alpha})`;
          ctx.beginPath();
          ctx.arc(cx + (p.x - cx) * k, cy + (p.y - cy) * k, 1.5, 0, TAU);
          ctx.fill();
        }
      });
      placed.forEach((a, i) => {
        placed.slice(i + 1).forEach((b) => {
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d >= R * 0.2) return;
          ctx.strokeStyle = `rgba(${ink},${0.08 * (1 - d / (R * 0.2)) * alpha})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        });
      });
      placed.forEach((p) => {
        ctx.fillStyle = `rgba(${ink},${(0.18 + 0.4 * p.z) * alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5 + p.z * 2.4, 0, TAU);
        ctx.fill();
      });
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 80);
      glow.addColorStop(0, `rgba(232,114,90,${0.75 * alpha})`);
      glow.addColorStop(0.3, `rgba(232,114,90,${0.25 * alpha})`);
      glow.addColorStop(1, "rgba(232,114,90,0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, 80, 0, TAU);
      ctx.fill();
      ctx.fillStyle = `rgba(232,114,90,${alpha})`;
      ctx.beginPath();
      ctx.arc(cx, cy, 6 + (reduced ? 0 : Math.sin(t * 1.6) * 1.3), 0, TAU);
      ctx.fill();
      if (running && !reduced) state.raf = requestAnimationFrame(draw);
    };

    const size = () => {
      const { width, height } = fitCanvas(canvas, ctx);
      state.width = width;
      state.height = height;
      state.points = makePoints(width < 700 ? options.narrowPoints : options.points);
      if (!running || reduced) draw(performance.now());
    };

    size();
    window.addEventListener("resize", size);
    if (running && !reduced) state.raf = requestAnimationFrame(draw);
    return () => {
      window.removeEventListener("resize", size);
      cancelAnimationFrame(state.raf);
    };
  }, [variant, running]);

  return <canvas ref={ref} aria-hidden="true" className="pointer-events-none absolute inset-0 size-full" />;
}
